// MA-C17 — elapsed-time clock renderer (deferred in ADR-022).
//
// Draws TWO analog clock faces — start and end — so a "start → end / how
// long?" item gets a faithful picture instead of no visual at all. The
// child reads both faces and computes the elapsed duration themselves; the
// renderer NEVER draws the elapsed value as a numeral or a labeled arc (it
// only ever receives `times`, never `total` — the elapsed minutes value is
// carried on the mathVisual payload purely for contract parity with the
// other renderers, per ClockRenderer's own convention, but this component
// deliberately never destructures/reads it, so it is structurally
// incapable of leaking the answer).
//
// Registered under "elapsed_time_clock" (see rendererRegistry). Reads
// `times`: an array of exactly 2 {a,b} pairs (hours, minutes) — [0] = start,
// [1] = end, in that order.
//
// Deliberately self-contained (does NOT share code with ClockRenderer.tsx)
// — the two faces here are always rendered as literal <circle>/<line>
// children of the same <svg>, never as a nested component element, so a
// caller that walks the returned tree by node type (see
// src/components/__tests__/newRenderers.test.mjs's countType helper, which
// only counts LITERAL element types and never expands a child component)
// keeps working for both renderers unchanged.
//
// FAIL-SAFE: a missing/malformed `times` array (not exactly 2 well-formed
// entries) renders nothing (null) rather than a blank/garbled face.
import type { MathRendererProps } from "./rendererRegistry";

const FACE = "var(--color-bg-secondary, #f5efe3)";
const RIM = "var(--color-border-secondary, #cdbfae)";
const TICK = "var(--color-text-secondary, #7a6f60)";
const HOUR_HAND = "var(--pal-orange, #d97742)";
const MIN_HAND = "var(--pal-green, #5a9e6f)";
const LABEL = "var(--color-text-primary, #3d3228)";

const FACE_SIZE = 140; // viewBox edge per face (square)
const FC = FACE_SIZE / 2; // per-face center
const FR = FC - 6; // face radius
const GAP = 40; // horizontal gap between the two faces
const LABEL_H = 26; // vertical space reserved for the "Start"/"End" text

function point(cx: number, cy: number, frac: number, len: number): { x: number; y: number } {
  const ang = frac * 2 * Math.PI - Math.PI / 2;
  return { x: cx + len * Math.cos(ang), y: cy + len * Math.sin(ang) };
}

/** Normalize a possibly-missing/non-finite hour or minute into a safe
 *  in-range int, mirroring ClockRenderer's own fail-safe clamp. */
function safe(v: number | undefined, mod: number): number {
  const n = Number.isFinite(v as number) ? Math.floor(v as number) : 0;
  return ((n % mod) + mod) % mod;
}

function clockDisplay(hours: number, minutes: number): string {
  const displayH = hours === 0 ? 12 : hours;
  return `${displayH}:${String(minutes).padStart(2, "0")}`;
}

function ElapsedTimeClockRenderer({ times }: MathRendererProps) {
  if (!Array.isArray(times) || times.length !== 2) return null;

  const width = FACE_SIZE * 2 + GAP;
  const height = FACE_SIZE + LABEL_H;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      style={{ maxWidth: 340 }}
      role="img"
      aria-label={`Two clocks: start time ${clockDisplay(safe(times[0]?.a, 12), safe(times[0]?.b, 60))}, end time ${clockDisplay(safe(times[1]?.a, 12), safe(times[1]?.b, 60))}`}
    >
      {(["Start", "End"] as const).map((label, i) => {
        const hours = safe(times[i]?.a, 12);
        const minutes = safe(times[i]?.b, 60);
        const cx = FC + i * (FACE_SIZE + GAP);
        const cy = FC;
        const hourFrac = (hours + minutes / 60) / 12;
        const minFrac = minutes / 60;
        const hourTip = point(cx, cy, hourFrac, FR * 0.5);
        const minTip = point(cx, cy, minFrac, FR * 0.78);
        return (
          <g key={label}>
            <circle cx={cx} cy={cy} r={FR} fill={FACE} stroke={RIM} strokeWidth="3" />
            {Array.from({ length: 12 }).map((_, t) => {
              const outer = point(cx, cy, t / 12, FR - 3);
              const inner = point(cx, cy, t / 12, FR - (t % 3 === 0 ? 13 : 8));
              return (
                <line
                  key={t}
                  x1={inner.x}
                  y1={inner.y}
                  x2={outer.x}
                  y2={outer.y}
                  stroke={TICK}
                  strokeWidth={t % 3 === 0 ? 3 : 1.5}
                  strokeLinecap="round"
                />
              );
            })}
            <line x1={cx} y1={cy} x2={minTip.x} y2={minTip.y} stroke={MIN_HAND} strokeWidth="3" strokeLinecap="round" />
            <line x1={cx} y1={cy} x2={hourTip.x} y2={hourTip.y} stroke={HOUR_HAND} strokeWidth="4.5" strokeLinecap="round" />
            <circle cx={cx} cy={cy} r="4" fill={HOUR_HAND} />
            <text x={cx} y={height - 6} textAnchor="middle" fontSize="14" fontFamily="inherit" fill={LABEL}>
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default ElapsedTimeClockRenderer;
